import { useEffect, useRef } from 'react'
import { Eyebrow, PrismLight, Segments } from '@/components/ui'
import { call } from '@/content/site'
import { useInView } from '@/hooks/useInView'
import { cx } from '@/lib/cx'
import { clamp } from '@/lib/math'
import { onTick } from './motion'
import styles from './UnderTheHood.module.css'

const { underTheHood } = call

/** Seconds the pulse spends between two stages, and resting after the last. */
const HOP = 1.45
const REST = 1.3
/** Seconds a stage's prism ripple lasts after the pulse reaches it. */
const RIPPLE = 1.4

function easeInOutSine(t: number): number {
  return -(Math.cos(Math.PI * t) - 1) / 2
}

interface Geometry {
  centers: { x: number; y: number }[]
  vertical: boolean
}

/** Props for {@link UnderTheHood}. */
export interface UnderTheHoodProps {
  /** Reduced motion: no travelling pulse; every stage glows evenly. */
  reduced: boolean
}

/**
 * "Under the hood": the voice stack as a signal-flow diagram. A fine line joins the five
 * stages (horizontal on wide screens, vertical on phones); an iris pulse with a prism
 * ripple travels it continuously, and each stage brightens as the pulse passes.
 */
export function UnderTheHood({ reduced }: UnderTheHoodProps) {
  const flowRef = useRef<HTMLDivElement>(null)
  const ruleRef = useRef<HTMLSpanElement>(null)
  const trailRef = useRef<HTMLSpanElement>(null)
  const pulseRef = useRef<HTMLSpanElement>(null)
  const geometry = useRef<Geometry>({ centers: [], vertical: false })
  const inView = useInView(flowRef, { rootMargin: '5% 0px' })

  // The line runs exactly from the first stage's centre to the last's, in either direction.
  useEffect(() => {
    const flow = flowRef.current
    const rule = ruleRef.current
    if (!flow || !rule) return
    const dots = Array.from(flow.querySelectorAll<HTMLElement>('[data-dot]'))
    const measure = () => {
      const box = flow.getBoundingClientRect()
      const centers = dots.map((dot) => {
        const rect = dot.getBoundingClientRect()
        return { x: rect.left + rect.width / 2 - box.left, y: rect.top + rect.height / 2 - box.top }
      })
      const first = centers[0]
      const last = centers[centers.length - 1]
      if (!first || !last) return
      const vertical = Math.abs(last.y - first.y) > Math.abs(last.x - first.x)
      geometry.current = { centers, vertical }
      rule.dataset.axis = vertical ? 'y' : 'x'
      rule.style.left = `${first.x}px`
      rule.style.top = `${first.y}px`
      rule.style.width = vertical ? '' : `${last.x - first.x}px`
      rule.style.height = vertical ? `${last.y - first.y}px` : ''
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(flow)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const flow = flowRef.current
    const trail = trailRef.current
    const pulse = pulseRef.current
    if (reduced || !inView || !flow || !trail || !pulse) return

    const nodes = Array.from(flow.querySelectorAll<HTMLElement>('[data-node]'))
    const rings = nodes.map((node) => node.querySelector<HTMLElement>('[data-ring]'))
    const lit = new Float32Array(nodes.length)
    const written = new Float32Array(nodes.length).fill(-1)
    const arrivedAt = new Float32Array(nodes.length).fill(-10)
    const segments = nodes.length - 1
    const cycle = segments * HOP + REST
    let clock = 0
    let lastReached = -1

    const remove = onTick((dt) => {
      const { centers, vertical } = geometry.current
      if (centers.length < 2) return
      clock += dt
      const t = clock % cycle
      const travel = Math.min(t / HOP, segments)
      const segment = Math.min(Math.floor(travel), segments - 1)
      // Eased per hop, so the signal seems to pause and be processed at each stage.
      const s = segment + easeInOutSine(travel - segment)
      const from = centers[segment]
      const to = centers[segment + 1]
      const local = s - segment
      const x = from.x + (to.x - from.x) * local
      const y = from.y + (to.y - from.y) * local

      const fadeIn = clamp(t / 0.35)
      const fadeOut = 1 - clamp((t - segments * HOP) / (REST * 0.6))
      pulse.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0)`
      pulse.style.opacity = (fadeIn * fadeOut).toFixed(3)
      const along = (s / segments).toFixed(4)
      trail.style.transform = vertical ? `scaleY(${along})` : `scaleX(${along})`
      trail.style.opacity = fadeOut.toFixed(3)

      // Arrival at a stage: start its prism ripple once per pass.
      if (t < dt * 1.5) lastReached = -1
      const reached = Math.round(s)
      if (Math.abs(s - reached) < 0.06 && reached > lastReached && t <= segments * HOP + dt) {
        lastReached = reached
        arrivedAt[reached] = clock
      }

      for (let i = 0; i < nodes.length; i++) {
        const proximity = Math.exp(-(((s - i) / 0.38) ** 2)) * fadeOut
        lit[i] = Math.max(lit[i] * Math.exp(-dt * 1.4), proximity)
        const shown = lit[i] < 0.004 ? 0 : lit[i]
        if (Math.abs(shown - written[i]) > 0.002 || (shown === 0 && written[i] !== 0)) {
          written[i] = shown
          nodes[i].style.setProperty('--lit', shown.toFixed(3))
        }
        const ring = rings[i]
        if (!ring) continue
        const age = (clock - arrivedAt[i]) / RIPPLE
        if (age >= 0 && age <= 1) {
          ring.style.transform = `scale(${(1 + age * 2.4).toFixed(3)})`
          ring.style.opacity = ((1 - age) ** 2).toFixed(3)
        } else if (ring.style.opacity !== '0') {
          ring.style.opacity = '0'
        }
      }
    })

    return remove
  }, [reduced, inView])

  return (
    <div className={cx('container', styles.root, reduced && styles.still)}>
      <PrismLight intensity={0.7} className={styles.light} />
      <div className={styles.panel}>
        <div className={styles.copy}>
          <div className={styles.heading}>
            <Eyebrow>{underTheHood.eyebrow}</Eyebrow>
            <h3 className={styles.title} data-reveal>
              <Segments segments={underTheHood.title} />
            </h3>
          </div>
          <p className={styles.body} data-reveal>
            {underTheHood.body}
          </p>
        </div>

        <div ref={flowRef} className={styles.flow}>
          <span ref={ruleRef} className={styles.rule} aria-hidden="true">
            <span ref={trailRef} className={styles.trail} />
          </span>
          <ol className={styles.nodes}>
            {underTheHood.flow.map((stage, index) => (
              <li key={index} className={styles.node} data-node>
                <span className={styles.slot} aria-hidden="true">
                  <span className={styles.dot} data-dot>
                    <span className={styles.ring} data-ring />
                    <span className={styles.core} />
                  </span>
                </span>
                <span className={styles.text}>
                  <span className={cx(styles.index, 'tabular')} aria-hidden="true">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className={styles.name}>{stage}</span>
                </span>
              </li>
            ))}
          </ol>
          <span ref={pulseRef} className={styles.pulse} aria-hidden="true">
            <span className={styles.pulseRipple} />
            <span className={styles.pulseRipple} />
            <span className={styles.pulseCore} />
          </span>
        </div>
      </div>
    </div>
  )
}
